/**
 * Mobile WebView Detection Utilities
 * 
 * This module provides utilities to detect when the web application is running
 * within the JamPackd mobile app's WebView environment and handle mobile-specific
 * authentication flows.
 */

/**
 * Detects if the application is running within the JamPackd mobile app WebView
 * @returns {boolean} True if running in JamPackd mobile app WebView
 */
export const isMobileWebView = (): boolean => {
  // Check for JamPackd mobile app user agent
  const userAgent = navigator.userAgent;
  const isJamPackdMobile = userAgent.includes('JamPackdCarePackages');
  
  // Additional checks for mobile WebView characteristics
  const isMobile = /iPhone|iPad|iPod|Android/i.test(userAgent);
  const isWebView = userAgent.includes('Mobile') && !userAgent.includes('Safari');
  
  // Return true only if it's specifically the JamPackd mobile app
  return isJamPackdMobile;
};

/**
 * Detects if the current environment is a standard web browser (desktop or mobile)
 * @returns {boolean} True if running in a standard browser (not WebView)
 */
export const isStandardBrowser = (): boolean => {
  return !isMobileWebView();
};

/**
 * Gets the current environment type
 * @returns {'mobile-webview' | 'browser'} The current environment type
 */
export const getEnvironmentType = (): 'mobile-webview' | 'browser' => {
  return isMobileWebView() ? 'mobile-webview' : 'browser';
};

/**
 * Checks if mobile authentication bridge is available
 * @returns {boolean} True if the AuthChannel is available for communication with Flutter
 */
export const isMobileAuthBridgeAvailable = (): boolean => {
  return (
    isMobileWebView() && 
    typeof (window as any).AuthChannel !== 'undefined' &&
    typeof (window as any).AuthChannel.postMessage === 'function'
  );
};

/**
 * Logs environment information for debugging
 * Logs the current environment type and available features
 */
export const logEnvironmentInfo = (): void => {
  // Environment info logging removed for production
};